import React, { useEffect, useState } from 'react';
import HdModal from '../../../../modal/components/modal/HdModal';
import {
  HdButton,
  HdFileInput,
  HdIcon,
  HdIconButton,
  HdPane
} from '../../../../components';
import { AuthorizationAccount } from '../../../connector-form/interfaces/auth';
import { checkAllRequiredFieldsPresent } from '../../../connector-form/components/google-auth/utils';
import { REQUIRED_SERVICE_ACC_KEY_FIELDS } from '../../../connector-form/components/google-auth/constants';
import styles from './EditServiceAccountDialog.module.scss';
import AuthAPI from '../../../../auth/api/AuthAPI';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';

export interface EditServiceAccountDialogProps {
  open: boolean;
  closeModelHandler: Function;
  oldServiceAccount: AuthorizationAccount;
  provider: any;
}

export const dataIdGenerator = getDataIdGenerator('auth-select-type');

export function EditServiceAccountDialog({
  open,
  closeModelHandler,
  provider,
  oldServiceAccount
}: EditServiceAccountDialogProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [serviceAccountEmail, setServiceAccountEmail] = useState(null);

  const handleUpdateClick = () => {
    setIsUpdating(true);
    AuthAPI.editServiceAccount(provider, oldServiceAccount.id, selectedFile.content)
      .then(() => {
        closeModelHandler();
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  const onFileChange = file => {
    setErrorMessage(null);
    setServiceAccountEmail(null);
    setSelectedFile(file);

    if (!file) {
      return;
    }

    const serviceAccount = file;
    let parsedServiceAccount;
    try {
      parsedServiceAccount = JSON.parse(serviceAccount.content);
    } catch (err) {
      setErrorMessage('Please upload a valid JSON file.');
      return;
    }

    if (!checkAllRequiredFieldsPresent(parsedServiceAccount, REQUIRED_SERVICE_ACC_KEY_FIELDS)) {
      setErrorMessage('Please upload a valid JSON file.');
      return;
    }

    setServiceAccountEmail(parsedServiceAccount.client_email);

    if (parsedServiceAccount.client_email !== oldServiceAccount.getDisplayName()) {
      setErrorMessage(
        'The key file you have uploaded is associated with a different service account.'
      );
    }
  };

  const isUpdateDisabled = !serviceAccountEmail || !!errorMessage;

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setServiceAccountEmail(null);
      setErrorMessage(null);
      setIsUpdating(false);
    }
  }, [open]);

  return (
    <HdModal
      disableRestoreFocus
      open={open}
      onClose={() => closeModelHandler()}
      data-id={dataIdGenerator('edit-service-account-dialog')}
    >
      <div className={`dialog-content ${styles.dialogContainer}`}>
        <div className='dialog-header border-bottom-0'>
          <div className='dialog-title'>Edit Service Account Configuration</div>

          <HdIconButton
            dataId={dataIdGenerator('close')}
            className={styles.closeBtn}
            onClick={() => closeModelHandler()}
          >
            <HdIcon name='close' className='actionable-icon-lg text-default' />
          </HdIconButton>
        </div>

        <div className='dialog-body'>
          <HdFileInput
            dataId={dataIdGenerator('service-account')}
            label='Service Account Key(JSON) *'
            accept='application/json'
            id='auth-edit-file'
            onChangeEventHandler={onFileChange}
          />

          {!!serviceAccountEmail && (
            <span data-id={dataIdGenerator('service-account-email')}>
              Your service account email id is{' '}
              <span className='text-default text-bold'>{serviceAccountEmail}</span>
            </span>
          )}

          {!!errorMessage && (
            <HdPane
              className='mt-2'
              variant='error-faded'
              icon='error-filled'
              dataId={dataIdGenerator('service-account-error')}
            >
              {errorMessage}
            </HdPane>
          )}
        </div>

        <div className={`text-medium ${styles.bottomContainer} p-5`}>
          <HdButton
            dataId={dataIdGenerator('cancel')}
            variation='outline'
            className='mr-3'
            onClick={() => closeModelHandler()}
          >
            Cancel
          </HdButton>

          <HdButton
            dataId={dataIdGenerator('update')}
            disabled={isUpdateDisabled}
            showProgress={isUpdating}
            onClick={() => handleUpdateClick()}
          >
            Update
          </HdButton>
        </div>
      </div>
    </HdModal>
  );
}
